import { Container } from '@components/common/Container';
import {
  AffordabilityCalculatorForm,
  Applicant,
  BenefitsAndCredits,
  CurrencyPeriodEnum,
  Income,
  IncomeKeys,
  MainIncomeType,
  OtherIncome,
} from '@pages/CalculatorPage/types';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { PageURL } from '@router/pageURL';
import {
  benefitsAndCreditsFieldsToCount,
  incomeFieldsToCount,
  NUMBER_OF_SIMILAR_FIELDS,
  otherIncomeFieldsToCount,
} from './helpers';

export const ApplicantIncomeFooter = () => {
  const intl = useIntl();
  const form = useFormContext<AffordabilityCalculatorForm>();
  const formFields = form.watch();

  const fieldsToFindLowestValue: IncomeKeys[] = [
    'basicAnnualSalaryRecentYear',
    'basicAnnualSalaryPreviousYear',
    'dividendsRecentYear',
    'dividendsPreviousYear',
  ];
  const fieldsAndTheirPrevYearCounterparts: Record<string, IncomeKeys> = {
    basicAnnualSalaryRecentYear: 'basicAnnualSalaryPreviousYear',
    basicAnnualSalaryPreviousYear: 'basicAnnualSalaryRecentYear',
    dividendsRecentYear: 'dividendsPreviousYear',
    dividendsPreviousYear: 'dividendsRecentYear',
    netTaxableIncomeRecentYear: 'netTaxableIncomePreviousYear',
    netTaxableIncomePreviousYear: 'netTaxableIncomeRecentYear',
  };

  const getValueOfField = (applicant: Applicant, field: IncomeKeys, incomeType: MainIncomeType) => {
    const periodKey = (field + 'Period') as keyof Income;
    const period = applicant?.[incomeType]?.[periodKey];
    const multiplier = period === CurrencyPeriodEnum.perMonth ? 12 : 1;
    const income = Number(applicant?.[incomeType]?.[field as keyof Income] || 0) || 0;

    return income * multiplier;
  };

  const findLowestValueBetweenTwoYears = (applicant: Applicant, field: IncomeKeys, incomeType: MainIncomeType) => {
    const recentYearValue = getValueOfField(applicant, field, incomeType);
    const previousYearValue = getValueOfField(applicant, fieldsAndTheirPrevYearCounterparts[field], incomeType);

    return recentYearValue === 0 || previousYearValue === 0
      ? Math.max(recentYearValue, previousYearValue) / NUMBER_OF_SIMILAR_FIELDS
      : Math.min(recentYearValue, previousYearValue) / NUMBER_OF_SIMILAR_FIELDS;
  };

  const countMainIncome = (applicant: Applicant, incomeType: MainIncomeType) => {
    return incomeFieldsToCount.reduce((acc, field) => {
      const fieldValue = fieldsToFindLowestValue.includes(field)
        ? findLowestValueBetweenTwoYears(applicant, field, incomeType)
        : getValueOfField(applicant, field, incomeType);

      return acc + fieldValue;
    }, 0);
  };

  const countBenefitsAndCredits = (applicant: Applicant) => {
    return benefitsAndCreditsFieldsToCount.reduce((acc, field) => {
      const every4WeeksMultiplier = 13;
      const income = field ? Number(applicant.benefitsAndCredits?.[field as keyof BenefitsAndCredits] || 0) : 0;

      return acc + income * every4WeeksMultiplier;
    }, 0);
  };

  const countOtherIncome = (applicant: Applicant) => {
    return otherIncomeFieldsToCount.reduce((acc, field) => {
      const periodKey = (field + 'Period') as keyof OtherIncome;
      const period = applicant?.otherIncome?.[periodKey];
      const multiplier = period === CurrencyPeriodEnum.perMonth ? 12 : 1;
      const income = Number(applicant?.otherIncome?.[field as keyof OtherIncome] || 0) || 0;

      return acc + income * multiplier;
    }, 0);
  };

  const applicantsCombinedIncomes = formFields.applicants.reduce((acc, applicant) => {
    if (!applicant) return acc;
    const primaryIncome = countMainIncome(applicant, 'primaryIncome');
    const secondIncome = countMainIncome(applicant, 'secondIncome');
    const benefitsAndCredits = countBenefitsAndCredits(applicant);
    const otherIncome = countOtherIncome(applicant);

    return acc + primaryIncome + secondIncome + benefitsAndCredits + otherIncome;
  }, 0);

  const formattedIncomeString = intl.formatNumber(applicantsCombinedIncomes, {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const [incomeMainPart, incomeFractions] = formattedIncomeString.split('.');

  return (
    <footer className={'bg-night-sky xs:p-4 bottom-0 z-10 flex w-full items-center md:p-6'}>
      <Container maxWidth={'lg'}>
        <div className={'text-white'}>
          <p className={'font-satoshi-black text-2-1xl m-0 font-black'}>
            {incomeMainPart}.<span className={'text-lg'}>{incomeFractions}</span>
          </p>
          <p className={'font-satoshi-medium xs:text-sm m-0 text-base font-medium'}>
            <FormattedMessage
              id={'affordabilityCalculator.applicantDetails.footer.title'}
              values={{
                a: text => (
                  <a target={'_blank'} rel={'noreferrer'} href={PageURL.CRITERIA} className={'text-secondary'}>
                    {text}
                  </a>
                ),
              }}
            />
          </p>
        </div>
      </Container>
    </footer>
  );
};
